import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import React, { useCallback, useEffect, useState } from "react";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { getClientsList } from "../../app/features/client/clientSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { useSelector } from "react-redux";
import { DateObject } from "react-multi-date-picker";
import { addDays } from "date-fns";

const breadcrumb = ["ClientsMapView"];

const API_KEY = "AIzaSyC5zjiNawTT_56MaMgtDg5wFhFiJJejXQA";

const OPTIONS = {
  minZoom: 3,
  maxZoom: 4,
};

const containerStyle = {
  width: "95vw",
  height: "95vh",
};

const center = {
  lat: 47,
  lng: -101,
};

var markers = [
  { id: 1, latitude: 39.9526, longitude: -75.1652, name: "Philadelphia" },
  { id: 2, latitude: 42.3601, longitude: -71.0589, name: "Boston" },
];

const ClientsMapView = () => {
  const dispatch = useAppDispatch();

  const { isClientLoading, clientList } = useSelector(
    (state: RootState) => state.client
  );

  const [isFunctionRan, setIsFunctionRan] = useState<boolean>(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });
  const [map, setMap] = React.useState(null);

  const [markers, setMarkers] = useState<
    {
      position: { lat: number; lng: number };
    }[]
  >([]);

  // ***** Callbacks *****
  const getClientsData = useCallback(async () => {
    try {
      const endDate = new DateObject();
      const startDate = endDate.setDate(addDays(new Date(), -30));
      const response = await dispatch(
        getClientsList({
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          fdpId: "",
        })
      ).unwrap();

      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "clientList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientListException",
      });
    }
  }, [dispatch]);

  const geocodeAddress = useCallback(async (address: string) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${API_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return { lat: location.lat, lng: location.lng };
      }
      return null;
    } catch (error) {
      console.error("Error geocoding address:", error);
      return null;
    }
  }, []);

  const createMarkers = useCallback(async () => {
    const newMarkers = [];
    for (const client of clientList) {
      const address = `${client.billingAddress}, ${client.city}, ${client.state}, ${client.zipCode}`;
      const location = await geocodeAddress(address);
      if (location) {
        newMarkers.push({
          position: location,
          clientName: client.clientName,
          billingAddress: client.billingAddress,
          city: client.city,
          state: client.state,
          zipcode: client.zipCode,
        });
      }
    }
    setMarkers(newMarkers);
  }, [clientList, geocodeAddress]);

  // ***** UseEffects *****
  useEffect(() => {
    if (isLoaded && clientList.length > 0) {
      createMarkers();
    }
  }, [isLoaded, clientList, createMarkers]);

  useEffect(() => {
    setIsFunctionRan(true);
    if (isFunctionRan) {
      getClientsData();
    }

    return () => {
      setIsFunctionRan(false);
    };
  }, [getClientsData]);

  const onLoad = React.useCallback(function callback(map: any) {
    const bounds = new window.google.maps.LatLngBounds(center);
    map.fitBounds(bounds);
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback(map: any) {
    setMap(null);
  }, []);

  if (!isLoaded) {
    return null;
  }
  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      options={OPTIONS}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {markers.map((marker, index) => (
        <MarkerF key={index} position={marker.position} />
      ))}
    </GoogleMap>
  );
};

export default ClientsMapView;
